import Group from 'components/utils/Group'
import { intlDateFormatter, isPresentOrFutureDateString } from 'lib/datetime/dateUtils'
import LineLuxuryEscapesMonogramIcon from 'LuxKit/Icons/line/LineLuxuryEscapesMonogramIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import LoyaltyTierMessageBanner from 'loyalty/components/LoyaltyTierMessageBanner'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import { LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL, LOYALTY_BONUS_POINTS_INFO_MODAL_MESSAGE, LOYALTY_BONUS_POINTS_INFO_MODAL_MESSAGE_WITH_END_DATE } from 'loyalty/constants/loyaltyBonusPoints'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { determineOfferLoyaltyBonusPointsCampaignType } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { triplePointsEndDate } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeover.constants'
import { type TakeoverOptions, useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import styled from 'styled-components'
import { INTL_LONG_DATE } from 'constants/dateFormats'

interface Props {
  offer?: TakeoverOptions['offer']
}

const InfoModalLoyaltyTierPill = styled(LoyaltyTierPill)`
  flex-shrink: 0;
  * {
    white-space: nowrap;
  }
`

const InfoModalLoyaltyTierMessageBanner = styled(LoyaltyTierMessageBanner)`
  border-radius: ${props => props.theme.borderRadius.S};

  && {
    padding: ${rem(16)};
  }
`

/**
 * Powered from campaign tags on offers
 * Requires mapping of campaign tags to campaign type
 */
function LoyaltyInfoModalCampaignCallout({ offer }: Props) {
  const intl = useIntl()
  const [tier] = useCurrentLoyaltyTier()
  const currentSiteTakeover = useCurrentSiteTakeover(offer ? { offer } : {})
  const offerBonusPointsCampaignType = offer ? determineOfferLoyaltyBonusPointsCampaignType(offer) : undefined
  const bonusPointsInfoModalMessage = offerBonusPointsCampaignType ? LOYALTY_BONUS_POINTS_INFO_MODAL_MESSAGE[offerBonusPointsCampaignType] : undefined

  let campaignEndDate: string | undefined
  switch (offerBonusPointsCampaignType) {
    case 'triple':
      // Temporary one-off: remove this branch and triplePointsEndDate import once modal dates come from Labs or campaign metadata.
      campaignEndDate = isPresentOrFutureDateString(triplePointsEndDate) ? triplePointsEndDate : undefined
      break
    default:
      break
  }

  if (!currentSiteTakeover || !offerBonusPointsCampaignType || !bonusPointsInfoModalMessage) return null

  const bonusPointsInfoModalMessageWithEndDate = LOYALTY_BONUS_POINTS_INFO_MODAL_MESSAGE_WITH_END_DATE[offerBonusPointsCampaignType]
  const renderBoldMessage = (chunks: ReactNode) => <BodyText key="bonus-points-message" as="span" variant="medium" weight="bold">{chunks}</BodyText>

  const formattedCampaignEndDate = campaignEndDate ? intlDateFormatter(new Date(campaignEndDate), INTL_LONG_DATE, intl.locale) : undefined
  const bonusPointsPillText = intl.formatMessage(LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL[offerBonusPointsCampaignType])
  const hasCampaignEndDate = !!formattedCampaignEndDate
  const bonusPointsBannerText = hasCampaignEndDate ? intl.formatMessage(
    bonusPointsInfoModalMessageWithEndDate,
    {
      bold: renderBoldMessage,
      date: formattedCampaignEndDate,
    },
  ) : intl.formatMessage(bonusPointsInfoModalMessage)

  return <InfoModalLoyaltyTierMessageBanner tier={tier} density="compact" flat icon={false}>
    <Group direction="vertical" tabletDirection="horizontal" gap={8} tabletGap={16} tabletVerticalAlign="center">
      {!!bonusPointsPillText && <InfoModalLoyaltyTierPill icon={<LineLuxuryEscapesMonogramIcon size={12} />} kind="primary" tier={tier}>
        {bonusPointsPillText}
      </InfoModalLoyaltyTierPill>}
      {hasCampaignEndDate ? <BodyText variant="medium" as="div">{bonusPointsBannerText}</BodyText> : <BodyText variant="medium" weight="bold">{bonusPointsBannerText}</BodyText>}
    </Group>
  </InfoModalLoyaltyTierMessageBanner>
}

export default LoyaltyInfoModalCampaignCallout
